import { useState } from "react";
import { useGetRealWorldConnection } from "@packages/api/madlenAPI";
import { Expand, X, Lightbulb, Globe, Briefcase } from "lucide-react";
import { useTranslation } from "react-i18next";
import Text from "@core/text";
import { Button } from "@core/index";
import { ConnectionCard } from "@features/tools/components/connections/ConnectionCard";
import { RealWorldConnectionResponseDto } from "@packages/api/madlenAPI.schemas";
import Markdown from "react-markdown";
import SpinnerLoader from "../components/SpinnerLoader";

const RealWorldConnectionDetailComponent = ({
  connection,
  closeModal,
}: {
  connection: RealWorldConnectionResponseDto;
  closeModal: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <div
      className="fixed inset-0 z-[9999] overflow-hidden bg-black bg-opacity-75 dark:bg-opacity-90 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          closeModal();
        }
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <Text variant={"h1"}>
            {connection.title || t("connections.title")}
          </Text>
          <Button
            onClick={closeModal}
            variant="solid"
            size="icon"
            rounded="full"
            bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X size={24} className="text-gray-500 dark:text-gray-400" />
          </Button>
        </div>

        <div className="flex-1 overflow-auto p-6 modal-content">
          <ConnectionCard
            connections={connection.connections}
            courseName={connection.courseName}
            createdAt={connection.createdAt}
            gradeLevel={connection.gradeLevel}
            isImageLoading={false}
            prompt={connection.title || connection.prompt}
            imageUrl={connection.imageUrl}
            videoUrl={connection.videoUrl}
            id={connection.id.toString()}
            author={connection.authorName || ""}
          />
        </div>
      </div>
    </div>
  );
};

const RealWorldConnections = ({ id }: { id: number | null }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const { data, isLoading, isError } = useGetRealWorldConnection(id, {
    query: {
      refetchInterval: (data) => {
        // Check if we have actual data and connections
        if (
          data?.state.data?.connections &&
          Array.isArray(data.state.data?.connections) &&
          data.state.data?.connections.length > 0
        ) {
          return false;
        }
        return 2000;
      },
      enabled: !!id,
      staleTime: Infinity,
      cacheTime: 1000 * 60 * 30,
    },
  });

  // Check if we're actually loading or if we have no connections data
  const isContentLoading =
    isLoading ||
    !data ||
    !data.connections ||
    !Array.isArray(data.connections) ||
    data.connections.length === 0;

  const handleExpandClick = () => {
    setExpanded(true);
  };

  const closeModal = () => {
    setExpanded(false);
  };

  if (isContentLoading) {
    return <SpinnerLoader />;
  }

  if (isError) {
    return (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm mb-6">
        <h3 className="text-xl font-medium mb-4 text-red-500 dark:text-red-400">
          {t("connections.error.unableToLoad")}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
          {t("connections.error.tryRegenerate")}
        </p>
      </div>
    );
  }

  return (
    <>
      {expanded && data && (
        <RealWorldConnectionDetailComponent
          connection={{
            ...data,
            gradeLevel: data.gradeLevel === -1 ? 0 : data.gradeLevel,
          }}
          closeModal={closeModal}
        />
      )}

      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm mb-6">
        <div className="flex w-full justify-between items-center mb-4">
          <h3 className="text-xl font-medium flex items-center text-gray-900 dark:text-gray-100">
            {t("connections.title")}
          </h3>
          <button
            className="flex items-center justify-center p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition duration-200"
            onClick={handleExpandClick}
          >
            <Expand size={18} className="text-gray-400 dark:text-gray-500" />
          </button>
        </div>
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
          {t("connections.subtitle")}
        </p>

        {data &&
          data.connections &&
          Array.isArray(data.connections) &&
          data.connections.length > 0 && (
            <div className="grid grid-cols-1 gap-6">
              {data.connections.slice(0, 2).map(
                (conn, index) =>
                  conn && (
                    <div
                      key={index}
                      className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 overflow-hidden"
                    >
                      <div className="p-6">
                        {conn.title && (
                          <div className="flex items-center gap-4 mb-6">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                              {conn.title}
                            </h2>
                          </div>
                        )}

                        <div className="grid md:grid-cols-1 gap-8">
                          <div className="space-y-6">
                            {/* Context Description */}
                            {conn.context_description && (
                              <div>
                                <div className="flex items-center gap-3 mb-3">
                                  <div className="p-2 rounded-xl bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400">
                                    <Lightbulb size={20} />
                                  </div>
                                  <h3 className="font-semibold text-blue-600 dark:text-blue-400">
                                    {t("connections.context")}
                                  </h3>
                                </div>
                                <div className="leading-relaxed">
                                  <Markdown className="prose dark:prose-invert">
                                    {conn.context_description}
                                  </Markdown>
                                </div>
                              </div>
                            )}

                            {/* Real World Application */}
                            {conn.real_world_application && (
                              <div>
                                <div className="flex items-center gap-3 mb-3">
                                  <div className="p-2 rounded-xl bg-emerald-100 dark:bg-emerald-900/50 text-emerald-600 dark:text-emerald-400">
                                    <Globe size={20} />
                                  </div>
                                  <h3 className="font-semibold text-emerald-600 dark:text-emerald-400">
                                    {t("connections.connectionsApplication")}
                                  </h3>
                                </div>
                                <div className="leading-relaxed">
                                  <Markdown className="prose dark:prose-invert">
                                    {conn.real_world_application}
                                  </Markdown>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Professions */}
                          {conn.professions && conn.professions.length > 0 && (
                            <div>
                              <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 rounded-xl bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-400">
                                  <Briefcase size={20} />
                                </div>
                                <h3 className="font-semibold text-purple-600 dark:text-purple-400">
                                  {t("connections.professions")}
                                </h3>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {conn.professions.map(
                                  (profession, idx) =>
                                    profession && (
                                      <Text
                                        key={idx}
                                        variant={"span"}
                                        size="sm"
                                        className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-700"
                                      >
                                        <Markdown>{profession}</Markdown>
                                      </Text>
                                    ),
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ),
              )}
            </div>
          )}
      </div>
    </>
  );
};

export default RealWorldConnections;
